"use client"

import type React from "react"

import { useState, forwardRef, useEffect } from "react"
import { Eye, EyeOff } from "lucide-react"

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string
  error?: string
  className?: string
  fullWidth?: boolean
  showStrengthIndicator?: boolean
  helperText?: string
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      error,
      className = "",
      fullWidth = false,
      showStrengthIndicator = false,
      value = "",
      helperText,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | "">("")
    const [strengthText, setStrengthText] = useState("")

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword)
    }

    useEffect(() => {
      if (showStrengthIndicator && typeof value === "string") {
        // Check for password strength
        const hasLowerCase = /[a-z]/.test(value)
        const hasUpperCase = /[A-Z]/.test(value)
        const hasNumber = /\d/.test(value)
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value)
        const isLongEnough = value.length >= 8

        const strengthScore = [hasLowerCase, hasUpperCase, hasNumber, hasSpecialChar, isLongEnough].filter(
          Boolean,
        ).length

        if (value.length === 0) {
          setPasswordStrength("")
          setStrengthText("")
        } else if (strengthScore <= 2) {
          setPasswordStrength("weak")
          setStrengthText("Débil - Añade caracteres especiales y números")
        } else if (strengthScore <= 4) {
          setPasswordStrength("medium")
          setStrengthText("Moderada - Buen progreso")
        } else {
          setPasswordStrength("strong")
          setStrengthText("Fuerte - Excelente contraseña")
        }
      }
    }, [value, showStrengthIndicator])

    const strengthColors = {
      weak: "bg-red-500",
      medium: "bg-yellow-500",
      strong: "bg-green-500",
    }

    const strengthWidth = {
      weak: "w-1/3",
      medium: "w-2/3",
      strong: "w-full",
    }

    const inputClasses = `
      px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 pr-10
      ${error ? "border-red-500 focus:ring-red-500" : "border-gray-300"} 
      ${fullWidth ? "w-full" : ""} 
      ${props.disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}
      ${className}
    `

    return (
      <div className={`${fullWidth ? "w-full" : ""} space-y-1.5`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            type={showPassword ? "text" : "password"}
            className={inputClasses}
            value={value}
            {...props}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={togglePasswordVisibility}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : helperText ? (
          <p className="text-sm text-gray-500">{helperText}</p>
        ) : null}

        {showStrengthIndicator && passwordStrength && (
          <div className="mt-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700">{strengthText}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-1.5 rounded-full transition-all duration-300 ${strengthColors[passwordStrength]} ${strengthWidth[passwordStrength]}`}
              ></div>
            </div>
          </div>
        )}
      </div>
    )
  },
)

PasswordInput.displayName = "PasswordInput"

export default PasswordInput

